import classNames from 'classnames'
import styles from './DonationListItem.module.css'
import useHeightObserver from './useHeightObserver'
import { CSSProperties, ForwardedRef, ReactNode, forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { Card } from '../Card'
import { IconButton } from '../IconButton'
import { LayoutStack } from '../layouts'
import { ListDonationListItemProps, ListDonationProps, ListSkeletonProps } from './types'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { TextLink } from '../TextLink'

function SkeletonInner() {
  return (
    <>
      <LoadingSkeleton width="6.25rem" height="3rem" />
      <LayoutStack space="200">
        <LoadingSkeleton width="5.7rem" height="1.3rem" />
        <LoadingSkeleton width="5.7rem" height="1.1rem" />
      </LayoutStack>
    </>
  )
}

function SkeletonWithoutHeightChange({ className }: { className?: string }) {
  return (
    <Card className={classNames(styles.card, className)}>
      <SkeletonInner />
    </Card>
  )
}

const SkeletonWithHeightChange = forwardRef(function SkeletonWithHeightObserver(
  { className, style }: ListSkeletonProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <div style={{ ...(style as CSSProperties), overflow: 'visible' }} ref={ref}>
      <Card className={classNames(styles.card, className)}>
        <SkeletonInner />
      </Card>
    </div>
  )
})

const Skeleton = forwardRef(function Skeleton(props: ListSkeletonProps, ref?: ForwardedRef<HTMLDivElement>) {
  if (props.onChangeSize) return <SkeletonWithHeightChange ref={ref} {...props} />

  return <SkeletonWithoutHeightChange {...props} />
})

function Cta({
  className,
  amount,
  ctaText,
  ctaLink,
}: {
  className?: string
  amount: ReactNode
  ctaText: string
  ctaLink: string
}) {
  return (
    <Card className={classNames(styles.card, className)}>
      <div className={classNames(styles.amountContainer, styles.ctaAmountContainer)}>{amount}</div>
      <div className={styles.ctaContainer}>
        <TextLink href={ctaLink}>{ctaText}</TextLink>
      </div>
    </Card>
  )
}

function Comment({
  comment,
  showMoreLabel,
  showLessLabel,
  maxPreviewLines = 2,
}: {
  comment: string
  showMoreLabel: string
  showLessLabel: string
  maxPreviewLines?: number
}) {
  const [showMore, setShowMore] = useState(false)
  const [isTooLong, setIsTooLong] = useState(false)
  const label = showMore ? showLessLabel : showMoreLabel
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      setIsTooLong(false)
      return
    }

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const neededLines = Math.ceil(element.scrollHeight / lineHeight)

    setIsTooLong(neededLines > maxPreviewLines)
  }, [maxPreviewLines, setIsTooLong])

  const toggleShowMore = useCallback(() => {
    setShowMore((prev) => !prev)
  }, [])
  return (
    <div className={styles.commentContainer}>
      <div
        ref={ref}
        className={classNames(styles.comment, { [styles.expanded!]: showMore })}
        style={{ '--max-preview-lines': maxPreviewLines } as CSSProperties}
      >
        {comment}
      </div>
      {isTooLong && (
        <IconButton
          kind="plain"
          size="small"
          iconName={showMore ? 'chevronUp' : 'chevronDown'}
          iconPosition="right"
          onClick={toggleShowMore}
          className={styles.showMoreButton}
        >
          {label}
        </IconButton>
      )}
    </div>
  )
}

function DonationListItemInner({
  amount,
  heading,
  timestamp,
  comment,
  showMoreLabel,
  showLessLabel,
  maxPreviewLines,
}: ListDonationProps) {
  return (
    <>
      <div className={styles.amountContainer}>
        <div>{amount}</div>
      </div>
      <LayoutStack space="none">
        <div className={styles.heading}>{heading}</div>
        <div className={styles.timestamp}>{timestamp}</div>
        {comment && (
          <Comment
            comment={comment}
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
            maxPreviewLines={maxPreviewLines}
          />
        )}
      </LayoutStack>
    </>
  )
}

function DonationListItemInnerWithoutHeightObserver({ className, ...props }: ListDonationProps) {
  return (
    <Card className={classNames(styles.card, className)}>
      <DonationListItemInner {...props} />
    </Card>
  )
}

const DonationListItemWithHeightObserver = forwardRef(function DonationListItemWithHeightObserver(
  { className, style, onChangeSize, ...props }: ListDonationProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const localRef = useHeightObserver({ style, onChangeSize })

  return (
    <div style={{ ...(style as CSSProperties), overflow: 'visible' }} ref={ref}>
      <Card className={classNames(styles.card, className)} ref={localRef}>
        <DonationListItemInner {...props} />
      </Card>
    </div>
  )
})

const DefaultDonationListItem = forwardRef(function DefaultDonationListItem(
  props: ListDonationProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  if (props.onChangeSize) return <DonationListItemWithHeightObserver ref={ref} {...props} />

  return <DonationListItemInnerWithoutHeightObserver {...props} />
})

const DonationListItem = forwardRef(function DonationListItem(
  { variant, amount, ctaText, ctaLink, className, ...props }: ListDonationListItemProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  if (variant === 'cta') return <Cta amount={amount} ctaText={ctaText} ctaLink={ctaLink} className={className} />
  if (variant === 'skeleton') return <Skeleton ref={ref} className={className} {...(props as ListSkeletonProps)} />

  const donationProps = { variant, amount, ctaText, ctaLink, className, ...props } as ListDonationProps

  return <DefaultDonationListItem {...donationProps} />
})

export default DonationListItem
